import { Button } from '@/components/ui/button'
import { BillingModalCopyKey } from '@/contexts/billing'
import { usePermanentStore } from '@/contexts/permanentStore'
import { AuthState } from '@/contexts/slices/auth'
import useAppState from '@/contexts/state'
import { defaultReviewFeedback, useTemporaryStore } from '@/contexts/temporaryStore'
import { useBilling } from '@/hooks/useBilling'
import { user as userService } from '@/services/user/user-service'
import { Icon } from '@iconify/react'
import { EngineAiOutput } from '@interfaces/ai'
import { ReviewFeedback } from '@interfaces/document'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $createParagraphNode,
  $createRangeSelection,
  $createTextNode,
  $getRoot,
  $isElementNode,
  $setSelection,
  LexicalNode,
} from 'lexical'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { useParams } from 'react-router-dom'
import { CREATE_SUGGESTION_COMMAND } from '../edit/plugins/SuggestionPlugin'
import { handleApplySingleImprovement, selectTextInBlock, updateSuggestionContent } from '../edit/utils'

interface Props {
  risk: EngineAiOutput['riskCards'][number]
  documentSummary: string
  versionNumber: number
  onApply: (appliedRisk: EngineAiOutput['riskCards'][number]) => void
  riskIndex?: number
}

// Helper function to strip HTML tags
const stripHtmlTags = (html: string): string => {
  return html.replace(/<[^>]*>/g, '')
}

export default function ApplyImprovementBtn({ risk, documentSummary, versionNumber, onApply }: Props) {
  const [editor] = useLexicalComposerContext()
  const [isLoading, setIsLoading] = useState(false)
  const [aiResponseHtml, setAiResponseHtml] = useState<string>('')
  const { id: documentPublicId } = useParams()
  const { onSubscribe } = useBilling()

  const { user: currentUser } = useAppState(s => ({
    user: (s as AuthState).user,
  }))

  const {
    setReviewFeedback,
    setShowReviewFeedbackDialog,
    setHideAiTooltip,
    addActiveProcessingBlock,
    removeActiveProcessingBlock,
  } = useTemporaryStore(s => ({
    setReviewFeedback: s.setReviewFeedback,
    setShowReviewFeedbackDialog: s.setShowReviewFeedbackDialog,
    setHideAiTooltip: s.setHideAiTooltip,
    addActiveProcessingBlock: s.addActiveProcessingBlock,
    removeActiveProcessingBlock: s.removeActiveProcessingBlock,
  }))

  const { setAutoFeedbackModalTimerStart } = usePermanentStore(s => ({
    setAutoFeedbackModalTimerStart: s.setAutoFeedbackModalTimerStart,
  }))

  const handleApplyImprovement = async () => {
    // Check if test mode is activated
    const url = new URL(window.location.href)
    const testMode = url.searchParams.get('test') === 'true'
    // --------------------------------------------

    // to avoid the ai tooltip from appearing, auto reverts to false after some time
    setHideAiTooltip(true)

    // auto select the text in the editor
    if (!risk.modified || risk.modified !== 'removed') {
      selectTextInBlock(editor, risk.blockId)
    } else {
      // create a new div node right underneath the node with blockId and select it
      editor.update(() => {
        const root = $getRoot()
        const children = root.getChildren()
        const targetBlock = children.find(
          (child: LexicalNode & { __blockId?: string }) => child.__blockId === risk.blockId
        )

        if (targetBlock && $isElementNode(targetBlock)) {
          const newDivNode = $createParagraphNode()
          targetBlock.insertAfter(newDivNode)
          newDivNode.append($createTextNode(' '))

          const selection = $createRangeSelection()
          selection.anchor.set(newDivNode.getKey(), 0, 'element')
          selection.focus.set(newDivNode.getKey(), newDivNode.getChildren().length, 'element')
          $setSelection(selection)
        }
      })
    }

    if (risk.status === 'APPLIED' && risk.appliedImprovement) {
      // Generate test suggestion ID
      const createdSuggestionId = `suggestion_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

      editor.dispatchCommand(CREATE_SUGGESTION_COMMAND, {
        suggestedHtml: '.',
        suggestionId: createdSuggestionId,
      })

      const words = risk.appliedImprovement.split(' ')
      let wordIndex = 0

      const testInterval = setInterval(() => {
        const currentWords = words.slice(0, wordIndex + 5).join(' ')
        updateSuggestionContent(editor, createdSuggestionId, currentWords)
        wordIndex++
        if (wordIndex >= words.length) {
          clearInterval(testInterval)
          toast.success('Improvement applied!')
        }
      }, 40)

      return
    }

    setIsLoading(true)

    // Clear any previous response
    setAiResponseHtml('')

    // Position processing indicator using the block element
    addActiveProcessingBlock(risk.blockId, 'Applying improvement...')

    handleApplySingleImprovement({
      editor,
      risk,
      documentVersionNumber: versionNumber,
      documentPublicId: documentPublicId || '',
      testMode,
      documentSummary: documentSummary,
      onStreamStart: () => {
        setAiResponseHtml('')
        setIsLoading(true)
      },
      onTextChunk: (text: string) => {
        setAiResponseHtml(text || '')
      },
      onStreamEnd: (finalText: string) => {
        setIsLoading(false)
        removeActiveProcessingBlock(risk.blockId)

        if (finalText) {
          setAiResponseHtml(finalText)

          toast.success('Improvement applied!')

          // Mark as applied locally
          onApply?.({ ...risk, appliedImprovement: finalText, status: 'APPLIED' })

          setAutoFeedbackModalTimerStart(new Date())
        } else {
          toast.error('Looks like we received an empty response. You might want to try again.')
        }
      },
      onStreamError: (error: any) => {
        setIsLoading(false)
        removeActiveProcessingBlock(risk.blockId)

        if (error.errorCode) {
          onSubscribe?.(error.errorCode as BillingModalCopyKey)
        } else {
          toast.error(error?.error ?? 'Something went wrong while applying the improvement.')
        }
      },
    })
  }

  // just in case
  if (!risk.blockId) return null

  // Show applied state
  if (risk.status === 'APPLIED') {
    return (
      <>
        <div className="me-2 flex items-center gap-1 text-default-600">
          <Icon
            icon="lucide:thumbs-up"
            width={17}
            height={17}
            className="cursor-pointer hover:text-default-foreground"
            onClick={() => {
              userService.sendReviewFeedbackToServer({
                ...defaultReviewFeedback,

                feedback_source: 'thumbs_up',
                ai_tool_used: 'APPLY_IMPROVEMENT',
                selected_text: risk.originalText || '',
                ai_output: aiResponseHtml ? stripHtmlTags(aiResponseHtml) : risk.what + risk.why || 'N/A',
                user_id: currentUser.id,
                email: currentUser.email,
              })
            }}
          />
          <Icon
            icon="lucide:thumbs-down"
            width={17}
            height={17}
            className="cursor-pointer hover:text-default-foreground"
            onClick={() => {
              const reviewFeedback: ReviewFeedback = {
                ...defaultReviewFeedback,

                feedback_source: 'thumbs_down',
                ai_tool_used: 'APPLY_IMPROVEMENT',
                selected_text: risk.originalText || '',
                ai_output: aiResponseHtml ? stripHtmlTags(aiResponseHtml) : risk.what + risk.why || 'N/A',
                user_id: currentUser.id,
                email: currentUser.email,
              }

              // first directly send the event
              userService.sendReviewFeedbackToServer(reviewFeedback)

              // now open the dialog
              setReviewFeedback(reviewFeedback)
              setShowReviewFeedbackDialog(true)
            }}
          />
        </div>

        <Button
          data-apply-btn="true"
          size="sm"
          variant="light"
          className="flex cursor-default gap-1"
          onClick={handleApplyImprovement}
        >
          Reapply improvement
        </Button>
      </>
    )
  }

  return (
    <Button
      size="sm"
      variant="light"
      className="flex gap-0.5"
      onClick={handleApplyImprovement}
      disabled={isLoading}
      data-apply-btn="true"
      data-improvement-applied="false"
    >
      <Icon
        icon={isLoading ? 'material-symbols:hourglass-top' : 'material-symbols:check-rounded'}
        width={20}
        height={20}
      />
      {isLoading ? 'Applying...' : 'Apply improvement'}
    </Button>
  )
}
